import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import History from 'src/components/History';
import { ChangelogUpdateType } from 'src/components/History/types';
import { RootStoreType } from 'src/types/store';
import api from '@api';
import { normalizeProviderHistoryUpdates } from './functions';

export interface HistoryPropsType {
    providerId: number | undefined;
}

const historyUpdates = [
    // Basic provider information
    { key: 'PUBLIC_ID', translateKey: 'providerDetail.publicId' },
    { key: 'ESHOP_KEY', translateKey: 'providerDetail.eshopKey' },
    { key: 'VIRTUAL_ESHOP_PARENT', translateKey: 'providerDetail.virtualEshopParent' },
    { key: 'COMPANY_NAME', translateKey: 'providerDetail.eshopName' },
    { key: 'WEBSITE', translateKey: 'providerDetail.providerWebsite' },
    { key: 'PROVIDER_EMAIL', translateKey: 'providerDetail.email' },
    { key: 'LOGO', translateKey: 'providerDetail.logo' },
    { key: 'ACTIVE', translateKey: 'providerDetail.shownInMys' },

    // Status and lifecycle fields
    { key: 'STATUS', translateKey: 'providerDetail.status' },
    { key: 'PLANNED_LAUNCH', translateKey: 'providerDetail.plannedLaunch', format: 'date'},
    { key: 'ACTUAL_LAUNCH', translateKey: 'providerDetail.actualLaunch', format: 'date' },
    { key: 'PLANNED_OFFBOARDING', translateKey: 'providerDetail.plannedOffboarding', format: 'date' },
    { key: 'ACTUAL_OFFBOARDING', translateKey: 'providerDetail.actualOffboarding', format: 'date' },

    // Address information
    { key: 'PROVIDER_STREET', translateKey: 'providerDetail.street' },
    { key: 'PROVIDER_CITY', translateKey: 'providerDetail.city' },
    { key: 'PROVIDER_STATE', translateKey: 'providerDetail.state' },
    { key: 'PROVIDER_POST_CODE', translateKey: 'providerDetail.postCode' },
    { key: 'PROVIDER_BILLING_STREET', translateKey: 'providerDetail.billingStreet' },
    { key: 'PROVIDER_BILLING_CITY', translateKey: 'providerDetail.billingCity' },
    { key: 'PROVIDER_BILLING_STATE', translateKey: 'providerDetail.billingState' },
    { key: 'PROVIDER_BILLING_POST_CODE', translateKey: 'providerDetail.billingPostCode' },

    // Contact information
    { key: 'PROVIDER_COMPANY_PHONE', translateKey: 'providerDetail.phone' },
    { key: 'CONTACT_PERSON', translateKey: 'providerDetail.contactPerson' },
    { key: 'BILLING_EMAIL', translateKey: 'providerDetail.billingEmail' },

    // Company details
    { key: 'PROVIDER_TAX_ID', translateKey: 'providerDetail.taxId' },
    { key: 'PROVIDER_COMPANY_ID', translateKey: 'providerDetail.companyId' },
    { key: 'BANK_ACCOUNT_NO', translateKey: 'providerDetail.bankAccountNo' },
    { key: 'GLN', translateKey: 'providerDetail.GLN' },
    { key: 'EORI', translateKey: 'providerDetail.eori' },

    // Business information
    { key: 'CLIENT_TYPE', translateKey: 'providerDetail.clientType' },
    { key: 'BUSINESS_NAME', translateKey: 'providerDetail.businessName' },
    { key: 'BUSINESS_FIELD', translateKey: 'providerDetail.businessField' },
    { key: 'SALES_REPRESENTATIVE', translateKey: 'providerDetail.salesRepresentative' },
    { key: 'KEY_ACCOUNT_MANAGER', translateKey: 'providerDetail.keyAccountManager' },

    // System and technical settings
    { key: 'SYSTEM', translateKey: 'providerDetail.system' },
    { key: 'STOCK_ID', translateKey: 'providerDetail.stockId' },
    { key: 'SHOPTET_STOCK_RATIO', translateKey: 'providerDetail.shoptetStockRatio', format: 'number' },

    // DCIx settings
    { key: 'DCIX_PROVIDER_ID', translateKey: 'providerDetail.dcixProviderId' },

    { key: 'EXTERNAL_DCIX_IMPORT', translateKey: 'providerDetail.externalDcixImport' },
    { key: 'UPDATE_ALL_STOCK', translateKey: 'providerDetail.updateAllStock' },
    { key: 'IMPORT_FROM_STATUS', translateKey: 'providerDetail.importFromStatus' },
    { key: 'SECONDARY', translateKey: 'providerDetail.secondary' },

    // Contract information
    { key: 'CONTRACT_SIGNING_DATE', translateKey: 'providerDetail.contractSigningDate' },
    { key: 'CONTRACT_TERM', translateKey: 'providerDetail.contractTerm' },
    { key: 'CONTRACT_END_DATE', translateKey: 'providerDetail.contractEndDate' },
    { key: 'INVOICE_DUE_DAYS', translateKey: 'providerDetail.invoiceDueDays', format: 'number' },
    { key: 'NOTICE_PERIOD_MONTHS', translateKey: 'providerDetail.noticePeriodMonths', format: 'number' },

    // SLA and service levels
    { key: 'B2C_SLA', translateKey: 'providerDetail.b2cSla', format: 'number' },
    { key: 'B2B_SLA', translateKey: 'providerDetail.b2bSla', format: 'number' },
    { key: 'B2B_D0', translateKey: 'providerDetail.b2bD0', format: 'number' },
    { key: 'B2B_D1', translateKey: 'providerDetail.b2bD1', format: 'number' },
    { key: 'B2B_D2', translateKey: 'providerDetail.b2bD2', format: 'number' },
    { key: 'B2B_D3', translateKey: 'providerDetail.b2bD3', format: 'number' },
    { key: 'SLA_ALZA_SHIPPING', translateKey: 'providerDetail.slaAlzaShipping', format: 'number' },
    { key: 'RECEIVING_POSTPONE_HOURS', translateKey: 'providerDetail.receivingPostponeHours', format: 'number' },

    // Email notifications
    { key: 'SEND_RECIPIENT_EMAIL_B2B_ON_THE_WAY', translateKey: 'providerDetail.sendRecipientEmailB2bOnTheWay' },
    { key: 'SEND_RECIPIENT_EMAIL_B2C_ON_THE_WAY', translateKey: 'providerDetail.sendRecipientEmailB2cOnTheWay' },

    // Feature flags and permissions
    { key: 'DEFAULT_PRINT_FILES', translateKey: 'providerDetail.printFiles' },
    { key: 'DEFAULT_ALLOW_EXPEDITION', translateKey: 'providerDetail.defaultAllowExpedition' },
    { key: 'ENABLE_RETURN_LABELS', translateKey: 'providerDetail.enableReturnLabels' },
    { key: 'PRINT_RETURN_LABEL', translateKey: 'providerDetail.printReturnLabel' },
    { key: 'ALLOW_ORDER_PRODUCT_DETAIL', translateKey: 'providerDetail.orderProductDetail' },
    { key: 'ALLOWED_ORDER_PRODUCTS_EDIT', translateKey: 'providerDetail.allowedOrderProductsEdit' },
    { key: 'UNDER_SANCTION_ENABLED', translateKey: 'providerDetail.underSanctionEnabled' },
    { key: 'BRANCH_ID_ENABLED', translateKey: 'providerDetail.branchIdEnabled' },
    { key: 'ALLOW_DISABLE_SHIPPING_REORDER', translateKey: 'providerDetail.allowDisableShippingReorder' },
    { key: 'CUSTOMS_WAREHOUSE_ENABLED', translateKey: 'providerDetail.customsWarehouseEnabled' },
    { key: 'DCIX_EXPORT_PERSONAL_DATA', translateKey: 'providerDetail.dcixExportPersonalData' },

    // Quality level and compensation fields - Receipt discounts
    { key: 'RECEIPT_DISCOUNT_12_24H', translateKey: 'providerDetail.receiptDiscount12to24', format: 'number' },
    { key: 'RECEIPT_DISCOUNT_24_48H', translateKey: 'providerDetail.receiptDiscount24to48', format: 'number' },
    { key: 'RECEIPT_DISCOUNT_48_72H', translateKey: 'providerDetail.receiptDiscount48to72', format: 'number' },
    { key: 'RECEIPT_DISCOUNT_72H_PLUS', translateKey: 'providerDetail.receiptDiscount72Plus', format: 'number' },

    // Return receipt discounts
    { key: 'RETURN_RECEIPT_DISCOUNT_12_24H', translateKey: 'providerDetail.returnReceiptDiscount12to24', format: 'number' },
    { key: 'RETURN_RECEIPT_DISCOUNT_24_48H', translateKey: 'providerDetail.returnReceiptDiscount24to48', format: 'number' },
    { key: 'RETURN_RECEIPT_DISCOUNT_48_72H', translateKey: 'providerDetail.returnReceiptDiscount48to72', format: 'number' },
    { key: 'RETURN_RECEIPT_DISCOUNT_72H_PLUS', translateKey: 'providerDetail.returnReceiptDiscount72Plus', format: 'number' },

    // B2C dispatch discounts
    { key: 'B2C_DISPATCH_DISCOUNT_97_95', translateKey: 'providerDetail.b2cDispatchDiscount9795', format: 'number' },
    { key: 'B2C_DISPATCH_DISCOUNT_94_90', translateKey: 'providerDetail.b2cDispatchDiscount9490', format: 'number' },
    { key: 'B2C_DISPATCH_DISCOUNT_89_LESS', translateKey: 'providerDetail.b2cDispatchDiscount89Less', format: 'number' },

    // B2B dispatch discounts
    { key: 'B2B_DISPATCH_DISCOUNT_0_100', translateKey: 'providerDetail.b2bDispatchDiscount0100', format: 'number' },
    { key: 'B2B_DISPATCH_DISCOUNT_101_999', translateKey: 'providerDetail.b2bDispatchDiscount101999', format: 'number' },
    { key: 'B2B_DISPATCH_DISCOUNT_1000_4000', translateKey: 'providerDetail.b2bDispatchDiscount10004000', format: 'number' },
    { key: 'B2B_DISPATCH_DISCOUNT_4001_PLUS', translateKey: 'providerDetail.b2bDispatchDiscount4001Plus', format: 'number' },

    // Quality metrics
    { key: 'ACCEPTABLE_LOSS_PERCENT', translateKey: 'providerDetail.acceptableLossPercent', format: 'number' },
    { key: 'ERROR_RATE_PERCENT', translateKey: 'providerDetail.errorRatePercent', format: 'number' },
];

const ProviderHistory: FC<HistoryPropsType> = (props) => {
    const [changelog, setChangelog] = useState<{ updates: ChangelogUpdateType[] } | null>(null);
    const [loading, setLoading] = useState(false);

    const boolNumberCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.boolNumberCodes);
    const clientTypeCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.clientTypeCodes);
    const businessFieldCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.businessFieldCodes);
    const contractTermCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.contractTermCodes);
    const providerSystemCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.providerSystemCodes);
    const keyAccountManagerCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.keyAccountManagerCodes);
    const salesRepresentativeCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.salesRepresentativeCodes);
    const providerStatusCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.providerStatusCodes);

    useEffect(() => {
        if (props.providerId) {
            loadHistory();
        }
    }, [props.providerId]);

    const loadHistory = async () => {
        if (!props.providerId) return;

        setLoading(true);
        try {
            const response = await api('get', `api/secure/providers/${props.providerId}/history`);
            // Axios wraps response in data, and API also wraps in data
            setChangelog({
                ...response.data.data,
                updates: normalizeProviderHistoryUpdates(response.data.data?.updates || []),
            });
        } catch (error) {
            console.error('Failed to load provider history:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <div>Loading history...</div>;
    }

    if (!changelog) {
        return <div>No history available</div>;
    }
    return (
        <History
            updates={changelog.updates}
            updateKeys={historyUpdates}
            updateItemsKeys={{
                FILES: {
                    itemKeys: [
                        { key: 'fileName', translateKey: 'providerDetail.fileName' },
                        { key: 'type', translateKey: 'providerDetail.fileType' },
                        { key: 'fileType', translateKey: 'providerDetail.fileExtension' },
                    ],
                },
                SERVICE_CODES: {
                    itemKeys: [{ key: 'LABEL', translateKey: 'providerDetail.serviceCodeLabel' }],
                },
            }}
            keyCodes={{
                // Boolean fields
                ACTIVE: boolNumberCodesObject,

                EXTERNAL_DCIX_IMPORT: boolNumberCodesObject,
                UPDATE_ALL_STOCK: boolNumberCodesObject,
                IMPORT_FROM_STATUS: boolNumberCodesObject,
                SECONDARY: boolNumberCodesObject,
                DEFAULT_PRINT_FILES: boolNumberCodesObject,
                DEFAULT_ALLOW_EXPEDITION: boolNumberCodesObject,
                SEND_RECIPIENT_EMAIL_B2B_ON_THE_WAY: boolNumberCodesObject,
                SEND_RECIPIENT_EMAIL_B2C_ON_THE_WAY: boolNumberCodesObject,
                ENABLE_RETURN_LABELS: boolNumberCodesObject,
                PRINT_RETURN_LABEL: boolNumberCodesObject,
                ALLOW_ORDER_PRODUCT_DETAIL: boolNumberCodesObject,
                ALLOWED_ORDER_PRODUCTS_EDIT: boolNumberCodesObject,
                UNDER_SANCTION_ENABLED: boolNumberCodesObject,
                BRANCH_ID_ENABLED: boolNumberCodesObject,
                ALLOW_DISABLE_SHIPPING_REORDER: boolNumberCodesObject,
                CUSTOMS_WAREHOUSE_ENABLED: boolNumberCodesObject,
                DCIX_EXPORT_PERSONAL_DATA: boolNumberCodesObject,

                // Dropdown fields
                CLIENT_TYPE: clientTypeCodesObject,
                BUSINESS_FIELD: businessFieldCodesObject,
                CONTRACT_TERM: contractTermCodesObject,
                SYSTEM: providerSystemCodesObject,
                KEY_ACCOUNT_MANAGER: keyAccountManagerCodesObject,
                SALES_REPRESENTATIVE: salesRepresentativeCodesObject,
                STATUS: providerStatusCodesObject,
            }}
            translatePrefix="providerDetail"
        />
    );
};

export default ProviderHistory;
